import React from 'react';
import PropTypes from 'prop-types';
import { noop } from 'src/utils/core/event';
import { removeInvalidXmlChars } from 'src/utils/helpers';
import s from './TextField.styles';

const TextField = ({
  className,
  fieldClassName,
  error,
  label,
  action,
  reset,
  hideReset,
  required,
  multiline,
  auth,
  value,
  rounded,
  type,
  mask,
  beforeMaskedStateChange,
  invisible,
  withPaddingLeft,
  inputRef,
  ...rest
}) => {
  let input = null;

  if (mask) {
    input = (
      <s.TextFieldInputMask
        beforeMaskedStateChange={beforeMaskedStateChange}
        mask={mask}
        value={value}
        {...rest}
      />
    );
  } else if (multiline) {
    input = (
      <s.TextFieldArea
        spellCheck={false}
        className={fieldClassName}
        error={!!error}
        ref={inputRef}
        type="text"
        value={value}
        {...rest}
        onChange={rest.onChange? (e)=>{
          e.target.value=removeInvalidXmlChars(e.target.value);
          rest.onChange(e);
        }: noop }
      />
    );
  } else {
    input = (
      <s.TextFieldInput
        auth={auth}
        className={fieldClassName}
        error={!!error}
        ref={inputRef}
        rounded={rounded}
        type={type || 'text'}
        value={value}
        {...rest}
        onChange={rest.onChange? (e)=>{
          e.target.value=removeInvalidXmlChars(e.target.value);
          rest.onChange(e);
        }: noop }
      />
    );
  }

  return (
    <s.TextField
      auth={auth}
      className={className}
      invisible={invisible}
      rounded={rounded}
      withPaddingLeft={withPaddingLeft}
      >
      {label && (
        <s.TextFieldLabel auth={auth}>
          {label}
          {required && <s.TextFieldRequired auth={auth}>*</s.TextFieldRequired>}
        </s.TextFieldLabel>
      )}
      {input}
      {action || ''}
      {reset && !hideReset && reset}
      {error && <s.TextFieldError auth={auth}>{error}</s.TextFieldError>}
    </s.TextField>
  );
};

TextField.propTypes = {
  action: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.node,
    PropTypes.string,
  ]),
  auth: PropTypes.bool,
  beforeMaskedStateChange: PropTypes.func,
  className: PropTypes.string,
  error: PropTypes.string,
  fieldClassName: PropTypes.string,
  hideReset: PropTypes.bool,
  invisible: PropTypes.bool,
  label: PropTypes.string,
  mask: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array
  ]),
  multiline: PropTypes.bool,
  onKeyPress: PropTypes.func,
  required: PropTypes.bool,
  reset: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.string,
    PropTypes.node,
  ]),
  rounded: PropTypes.bool,
  type: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  withPaddingLeft: PropTypes.bool,
  inputRef: PropTypes.object
};

TextField.defaultProps = {
  action: null,
  auth: false,
  beforeMaskedStateChange: ({ nextState }) => ({ ...nextState }),
  className: null,
  error: null,
  fieldClassName: null,
  hideReset: false,
  invisible: false,
  label: null,
  mask: null,
  onKeyPress: () => {},
  placeholder: null,
  required: null,
  reset: null,
  rounded: false,
  type: null,
  value: null,
  withPaddingLeft: false,
};

export default React.memo(TextField);
